'use client'
import { useParams } from 'next/navigation'
import { type FC, memo, useMemo } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { useDeviceType, useTranslator } from '@/hooks'
import { designGrid, font12, font15, font20, font25, mq, vw, createUnderline } from '@/styles'
import { Link } from '../base/Link'
import { Figure } from '../base/Figure'
import { Img } from '../base/Img'
import { getIdBySelfbookId } from './SectionRoomInfo'
import type { TypeCmsGrid } from '@/schema'

type Props = {
  readonly data: TypeCmsGrid
}

const getItems = (data, onClick = null) => data.map((item, idx) => {
  const { id, plpTitle, plpDescription, medium, plpFeatures, slug, selfbookId }= item

  return (
    <Li key={`${idx}-${id}`}>
      <article>
        <Link href={`/suite/${slug}?roomId=${getIdBySelfbookId(selfbookId)}`} rel='bookmark' {...onClick && { onClick }}>
          <Img media={medium?.image} lazy={idx > 3}/>
          <hgroup>
            <h3>{plpTitle}</h3>
            <p>{plpDescription}</p>
          </hgroup>
          <div dangerouslySetInnerHTML={{ __html: plpFeatures }} />
        </Link>
      </article>
    </Li>

  )
})

// const dataConnectedRoom = {
//   'en':'Privately connect our contiguous rooms to create larger, flexible spaces, perfect for families or groups',
//   'es': 'Conecte de manera privada nuestras habitaciones contiguas para crear espacios amplios y flexibles, perfectos para familias o grupos',
//   'fr': 'Connectez nos chambres adjacentes de manière privée pour créer des espaces plus grands et flexibles, parfaits pour les familles ou les groupes.',
//   'de': 'Verbinden Sie unsere angrenzenden Zimmer privat, um größere, flexible Räume zu schaffen - perfekt für Familien oder Gruppen.'
// }

export const SectionGrid:FC<Props> = memo(({ data }) => {
  // const { setImgData, startTransition } = useTransitionToSuite()
  const { t } = useTranslator()
  // const dataConnected = useMemo(() => data.filter(item => item.roomType === 'connected_room'), [data])
  const dataSuites = useMemo(() => data.filter(item => item.roomType === 'suite'), [data])
  const { isMobile } = useDeviceType()
  const { lang } = useParams() as { lang: string }
	// const ref = useRef<HTMLElement>(null)
	// const isInView = useInView(ref, { margin: '0px', once: true })

  // const onClick = e => {
  //   e.preventDefault()

  //   const link = e.currentTarget
  //   const linkHref = link.getAttribute('href')
  //   setHref(linkHref)

  //   const img = link.querySelector('img')
  //   const imgSrc = img.getAttribute('src')
  //   const imgSize = img.getBoundingClientRect()

  //   setImgData({
  //     src: imgSrc,
  //     size: {
  //       height: imgSize.height,
  //       width: imgSize.width,
  //       left: imgSize.left,
  //       top: imgSize.top
  //     }
  //   })
  // }

  // useEffect(() => {
  //   if(!href || !startTransition) return
  //   setTimeout(() => {
  //     window.scrollTo({ top: 0, behavior: 'smooth' })
  //     push(href)
  //   }, 300)
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [href, startTransition])

  const Items = useMemo(() => getItems(dataSuites) , [dataSuites])
  // const ConnectedItems = useMemo(() => getItems(dataConnected) , [dataConnected])
  const title = {
    en: 'ROOMS & SUITES',
    fr: 'CHAMBRES & SUITES',
    de: 'ZIMMER & SUITEN',
    es: 'HABITACIONES & SUITES',
  }

	const description = {
		en: 'Set within a 17th-century house, no two of our rooms are alike. Proportions, ceilings and details vary from room to room, so yours may differ slightly from the photographs.',
		fr: 'Situées dans une demeure du XVIIe siècle, nos chambres sont toutes uniques. Les proportions, les plafonds et les détails varient d\'une chambre à l\'autre. Votre chambre peut donc différer légèrement des photographies.',
		de: 'Unsere Zimmer befinden sich in einem Haus aus dem 17. Jahrhundert und jedes ist einzigartig. Raumproportionen, Decken und Details variieren von Zimmer zu Zimmer, sodass Ihr Zimmer leicht von den Fotografien abweichen kann',
		es: 'Ubicadas en una casa del siglo XVII, cada una de nuestras habitaciones es única. Las proporciones, los techos y los detalles varían de una habitación a otra, por lo que la suya puede diferir ligeramente de las fotografías.'
	}

  return (
    <>
      <Section $visible={true} >
        <hgroup>
          <h2>{title[lang ?? 'en']}</h2>
					<p>{description[lang ?? 'en']}</p>
        </hgroup>
        <ul>
          {Items}
          {Items.length % 2 !== 0 && !isMobile && <motion.li key='contact' className='contact'>
            <Figure media={{ alt: 'Window draw', m: { src: '/images/windows-plant.png', size: { width: 154, height: 158 } } }} />
            <h3>{t('project.aside.exclusive')}</h3>
            <p>
              {t('project.aside.book_all')} <a href='mailto:welcome@portellapalma.com.'>{t('project.aside.write_us')}</a> {t('project.aside.or_call')} <a href='tel:0034871626263'>{t('project.aside.phone')}</a>.
            </p>
          </motion.li>}
        </ul>
      </Section>
      {/* <Section $visible={true} ref={ref} variants={getVariants(isInView)} initial='initial' animate='animate' exit='exit'>
        <hgroup>
          <h2>CONNECTED ROOMS</h2>
          <p>{dataConnectedRoom[lang] ?? dataConnectedRoom['en']}</p>
        </hgroup>
        <ul>
          {ConnectedItems}
        </ul>
      </Section> */}
    </>
  )
})

const Section = styled(motion.section)<{ $visible: boolean }>`
  ${designGrid({})}
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: 250ms opacity ease-out;

  &:first-child {
    > hgroup {
      margin: ${vw(50, 'mobile')} auto;


      ${mq.greaterThan('tablet')} {
        margin: ${vw(70, 'desktop')} auto ${vw(76, 'desktop')};
      }

			h2 {
        margin-bottom: ${vw(10, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-bottom: ${vw(10, 'desktop')};
        }
      }
    }
  }

  + section {
    margin: ${vw(150, 'mobile')} 0 ${vw(70, 'mobile')};

    ${mq.greaterThan('tablet')} {
      margin: ${vw(144, 'desktop')} 0 ${vw(70, 'desktop')};
    }


    > hgroup {
      margin-bottom: ${vw(50, 'mobile')};

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(70, 'desktop')};
      }

      h2 {
        margin-bottom: ${vw(15, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-bottom: ${vw(15, 'desktop')};
        }

      }
    }
  }

  > hgroup {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    grid-column: 1 / span 6;
    justify-content: center;

    ${mq.greaterThan('tablet')} {
      align-items: center;
      flex-direction: column;
      flex-wrap: nowrap;
      grid-column: 5 / span 4;
      justify-content: center;
    }

    h2,
    p {
      text-align: center;
    }

    p {
      ${font25(true, 400)}
      letter-spacing: -.2px;

      ${mq.greaterThan('tablet')} {
        ${font20(false, 400)}
      }
    }
  }

  h2 {
    ${font20(true, 400)}
    text-transform: uppercase;

    ${mq.greaterThan('tablet')} {
      ${font20(false, 400)}
    }

    + p {
      ${font15(true, 400)}

      ${mq.greaterThan('tablet')} {
        ${font15(false, 400)}
      }
    }
  }


  ul {
    display: flex;
    flex-wrap: wrap;
    grid-column: 1 / span 6;
    row-gap: ${vw(70, 'mobile')};
    width: 100%;

    ${mq.greaterThan('tablet')} {
      column-gap: ${vw(30, 'desktop')};
      grid-column: 1 / span 12;
      row-gap: ${vw(70, 'desktop')};
    }

  }

  .contact {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${vw(85, 'mobile')} ${vw(10, 'mobile')} ${vw(135, 'mobile')};
    width: 100%;

    ${mq.greaterThan('tablet')} {
      padding: ${vw(90, 'desktop')} ${vw(50, 'desktop')} ${vw(140, 'desktop')};
      width: calc(50% - ${vw(15, 'desktop')});
    }

    figure {
      mix-blend-mode: multiply;
      height: ${vw(154, 'mobile')};
      width: ${vw(158, 'mobile')};

      ${mq.greaterThan('tablet')} {
        height: ${vw(154, 'desktop')};
        width: ${vw(158, 'desktop')};
      }
    }

    h3, p {
      text-align: center;
    }

    figure,
    h3 {
      margin-bottom: ${vw(22, 'mobile')};

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(22, 'desktop')};
      }
    }

    h3 {
      ${font20(true, 400)}

      ${mq.greaterThan('tablet')} {
        ${font20(false, 400)}
      }
    }

    p {
      ${font15(true, 400)}
      letter-spacing: -.2px;
      max-width: ${vw(460, 'mobile')};

      ${mq.greaterThan('tablet')} {
        ${font15(false, 400)}
        letter-spacing: unset;
        max-width: ${vw(460, 'desktop')};
      }
    }

    a {
      ${createUnderline({reverse: true})}
    }
  }
`

const Li = styled(motion.li)`
  width: 100%;

  ${mq.greaterThan('tablet')} {
    width: calc(50% - ${vw(15, 'desktop')});
  }

	&:hover {
		a {
			picture {
				img {
					transform: scale(1.05);
				}
			}
		}
	}

  a {
    > div {
      display: flex;
      flex-direction: column;
    }

    > img,
    .placeholder,
    picture {
      height: ${vw(200, 'mobile')};
      margin-bottom: ${vw(15, 'mobile')};
      width: 100%;

      ${mq.greaterThan('tablet')} {
        height: ${vw(380, 'desktop')};
        margin-bottom: ${vw(15, 'desktop')};
      }

      img {
        transform: scale(1);
        height: 100%;
        object-fit: cover;
        transition: transform 1s ease-in-out;

      }
    }

    .placeholder,
    picture {
      background-color: rgba(250, 250, 250);
      overflow: hidden;
    }
  }


  hgroup {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin: 0 ${vw(20, 'mobile')} ;
    min-height: ${vw(90, 'mobile')};

    ${mq.greaterThan('tablet')} {
      margin: 0 ${vw(50, 'desktop')} ;
      min-height: ${vw(90, 'desktop')};
    }
  }

  h3,
  p {
    text-align: center;
  }

  li,
  td {
    ${font12(true, 400)}

    ${mq.greaterThan('tablet')} {
      ${font12(false, 400)}
    }
  }

  h3 {
    ${font20(true, 400)}
    margin-bottom: ${vw(10, 'mobile')};
    text-transform: uppercase;

    ${mq.greaterThan('tablet')} {
      ${font20(false, 400)}
      margin-bottom: ${vw(10, 'desktop')};
    }
  }

  p {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    ${font15(true, 400)}
    letter-spacing: -.15px;
    margin-bottom: ${vw(15, 'mobile')};
    overflow: hidden;

    ${mq.greaterThan('tablet')} {
      ${font15(false, 400)}
      margin-bottom: ${vw(15, 'desktop')};
    }
  }

  ul,
  table {
    display: flex;
    justify-content: center;
    margin: 0 auto;
    gap: 0;
  }

  td,
  li {
    flex: 1;
    border-right: 1px solid rgba(0, 0, 0, .2);
    padding: 0 ${vw(20, 'mobile')};
    text-align: center;

    ${mq.greaterThan('tablet')} {
      padding: 0 ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 0 20px;
    }

    &:first-child {
      text-align: right;
    }

    &:last-child {
      border-right: 0;
      text-align: justify;
    }
  }
`
